// ============================================
// DataQuality - 数据质量监控组件
// 完整生产级实现
// ============================================

import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface QualityReport {
  period: {
    days: number
    startDate: string
    endDate: string
  }
  summary: {
    totalIssues: number
    avgFixMinutes: number
  }
  byType: Array<{
    issue_type: string
    total: number
    fixed: number
    manual_review: number
    pending: number
  }>
  affectedBids: Array<{
    bid_id: string
    issue_count: number
    last_issue_at: string
  }>
}

export default function DataQuality() {
  const [report, setReport] = useState<QualityReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(7)

  useEffect(() => {
    fetchQualityReport()
  }, [days])

  const fetchQualityReport = async () => {
    setLoading(true)
    try {
      const response = await fetch(`/api/quality?days=${days}`)
      const data = await response.json()
      if (data.success) {
        setReport(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch quality report:', error)
    } finally {
      setLoading(false)
    }
  }

  const triggerQualityCheck = async () => {
    try {
      const response = await fetch('/api/quality/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_fix: true,
          issue_types: ['missing_field', 'invalid_value', 'duplicate']
        })
      })
      const data = await response.json()
      if (data.success) {
        alert('质量检查任务已创建')
        fetchQualityReport()
      }
    } catch (error) {
      console.error('Failed to trigger quality check:', error)
      alert('触发失败')
    }
  }

  const getIssueTypeName = (type: string) => {
    const names = {
      duplicate: '重复数据',
      missing_field: '字段缺失',
      invalid_value: '值无效',
      inconsistent: '数据不一致',
      outdated: '数据过时'
    }
    return names[type as keyof typeof names] || type
  }

  if (loading) {
    return <div className="text-white">加载中...</div>
  }

  if (!report) {
    return <div className="text-white">暂无数据</div>
  }

  return (
    <div className="space-y-6">
      {/* 概览统计 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">
              总问题数
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">
              {report.summary.totalIssues}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              近{days}天
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">
              自动修复
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-400">
              {report.byType.reduce((sum, type) => sum + type.fixed, 0)}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              修复率: {report.summary.totalIssues > 0
                ? ((report.byType.reduce((sum, type) => sum + type.fixed, 0) / report.summary.totalIssues) * 100).toFixed(1)
                : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-400">
              平均修复时间
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-400">
              {report.summary.avgFixMinutes.toFixed(1)}分钟
            </div>
            <p className="text-xs text-slate-400 mt-1">
              自动修复平均耗时
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 按类型统计 */}
      <Card>
        <CardHeader>
          <CardTitle>问题分类统计</CardTitle>
          <CardDescription>按问题类型分组</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {report.byType.map((item) => (
              <div key={item.issue_type} className="border-b border-slate-700 pb-4 last:border-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-white">
                    {getIssueTypeName(item.issue_type)}
                  </div>
                  <div className="text-sm text-slate-400">
                    总计: {item.total}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">自动修复: </span>
                    <span className="text-green-400 font-medium">{item.fixed}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">人工复核: </span>
                    <span className="text-yellow-400 font-medium">{item.manual_review}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">待处理: </span>
                    <span className="text-red-400 font-medium">{item.pending}</span>
                  </div>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${item.total > 0 ? (item.fixed / item.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 受影响标的 */}
      <Card>
        <CardHeader>
          <CardTitle>受影响标的</CardTitle>
          <CardDescription>问题最多的标的前50个</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {report.affectedBids.slice(0, 20).map((bid) => (
              <div key={bid.bid_id} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                <div className="flex-1">
                  <div className="text-sm font-mono text-white truncate">
                    {bid.bid_id}
                  </div>
                  <div className="text-xs text-slate-400">
                    最后问题: {new Date(bid.last_issue_at).toLocaleString('zh-CN')}
                  </div>
                </div>
                <div className="ml-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    bid.issue_count > 5
                      ? 'bg-red-500/20 text-red-400'
                      : bid.issue_count > 2
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-green-500/20 text-green-400'
                  }`}>
                    {bid.issue_count} 个问题
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 操作 */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={days === 7 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDays(7)}
          >
            近7天
          </Button>
          <Button
            variant={days === 30 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDays(30)}
          >
            近30天
          </Button>
        </div>
        <Button onClick={triggerQualityCheck}>触发质量检查</Button>
      </div>
    </div>
  )
}
